/**
 * Small UPS section descriptor (#997 — Story 4.5).
 *
 * The real, live `SectionDescriptor` for the Small UPS dynamic section. It
 * mirrors the Power Distribution descriptor (Story 4.4) but binds the ACTUAL
 * Small UPS per-unit field set, table, mapper, REST contract, and attachment
 * cards, so the shared descriptor engine can drive Small UPS at byte-identical
 * parity with the pre-engine bespoke implementation (Story 4.5 golden masters
 * are the oracle).
 *
 * Field source of truth: `SMALL_UPS_FIELDS` (`small-ups.fields.ts`) plus the
 * editor slice at `SMALL_UPS_EDITOR_SUBSECTION_FIELDS[2..]` — every per-unit
 * field is typed here (text / multilineText / number / dropdown / yesNo) to
 * match the real controls in `SmallUpsEditFormContent.tsx`. `name` (user-defined
 * instance name) and `status` (Existing/New) are the engine's identity fields
 * and are handled by the engine list chrome, so they are NOT per-unit descriptor
 * `fields` (they are added back only on the Summary surface via the
 * identity-field label bridge in `smallUpsDescriptorSummary`).
 *
 * `powerSmallUpsAdditionalInfo` is a PER-UNIT "Additional Information" note on
 * `SmallUpsConfiguration` (migration 091), so it lives on the per-unit fields
 * list and is declared via `additionalInfoField`. Small UPS has NO shared
 * request-level field (mirrors LU and PD; contrast LC's
 * `lcLiquidToChipDesired`), so `sharedFieldKeys` is `[]`.
 *
 * Small UPS is the widest field-type stress case in Epic 4: it is the FIRST
 * descriptor to cover `dropdown` (4 fields), `number` (4 fields), AND `yesNo`
 * (2 fields) in a single section. LU exercised a single `yesNo`; PD exercised
 * `text` and `multilineText` only. The typed metadata is currently passive on this story's
 * surfaces (Summary / offline / DB-binding are type-agnostic — they read raw
 * values), but the deferred live-editor swap (Phase 4) is the first place these
 * types actually drive UI, so their fidelity to the live controls is
 * load-bearing.
 *
 * NFR7: the table (`request_small_ups`), mapper
 * (`small-ups-configuration.mapper`), and permission config are read-only;
 * this descriptor only DECLARES the binding.
 */

import { locationTagDescriptorField } from './sectionDescriptor.types';
import {
  SMALL_UPS_TABLE_COLUMN_ALIGN,
  formatSmallUpsTableCell,
  getSmallUpsColumnSubline,
  smallUpsColumnUsesDoubleLine,
  smallUpsTableColumnsForLayout,
  smallUpsTableHeaderLabel,
} from '../../components/smallups/smallUpsListUtils';
import { SECTION_IDS } from '../../constants/sectionIds';
import {
  SMALL_UPS_CONFIGURATIONS_LIST_TITLE,
  SMALL_UPS_DELETE_CONFIRM_MESSAGE,
  SMALL_UPS_DELETE_CONFIRM_TITLE,
  SMALL_UPS_DISCARD_UNSAVED_DISCARD_LABEL,
  SMALL_UPS_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
  SMALL_UPS_DISCARD_UNSAVED_MESSAGE,
  SMALL_UPS_DISCARD_UNSAVED_TITLE,
  SMALL_UPS_EDIT_ALL_UNITS_LABEL,
  SMALL_UPS_EDIT_DELETE_LABEL,
  SMALL_UPS_EDIT_HEADER_TITLE_NEW,
  SMALL_UPS_EDIT_SAVE_DRAFT_LABEL,
  SMALL_UPS_EDIT_SAVE_LABEL,
  SMALL_UPS_EMPTY_STATE_TITLE,
} from '../../constants/smallUpsConfigurationsSubsectionCopy';
import {
  SMALL_UPS_INPUT_PHASE_OPTIONS,
  SMALL_UPS_MOUNTING_OPTIONS,
  SMALL_UPS_MOUNT_TYPE_OPTIONS,
  SMALL_UPS_TYPE_OPTIONS,
} from '../../constants/smallUpsRequirementsUi';
import { SMALL_UPS_EDITOR_SUBSECTION_FIELDS } from '../fields/requestFieldRegistry';
import { SMALL_UPS_FIELDS } from '../fields/steps/small-ups.fields';

import type {
  SectionDescriptor,
  SectionDescriptorField,
  SectionDescriptorFieldType,
  SectionDescriptorFieldValidation,
  SectionListColumnsProjection,
} from './sectionDescriptor.types';
import type { SmallUpsTableColumnKey } from '../../components/smallups/smallUpsListUtils';
import type { SmallUpsConfiguration } from '../../types/smallUps';

/**
 * Per-field render type + validation + dropdown options, keyed by dataField.
 * Every non-identity per-unit key covered by the descriptor has an entry here;
 * anything missing falls back to `text`. Types match the real Small UPS
 * controls (see `SmallUpsEditFormContent.tsx`):
 *
 *   - `smallUpsManufacturer`         — FormInput (text) at :268
 *   - `smallUpsModel`                — FormInput (text) at :286
 *   - `powerSmallUpsDescription`     — FormInput multiline `maxLength={10000}`
 *     at :305 (multilineText)
 *   - `smallUpsSerialNumber`         — FormInput (text) at :325
 *   - `powerTypesOfUps`              — FormDropdownField at :346, options from
 *     `SMALL_UPS_TYPE_OPTIONS`
 *   - `smallUpsInputVoltage`         — FormInput (text) at :369
 *   - `smallUpsInputPlug`            — FormInput (text) at :390
 *   - `powerInputPowerPhase`         — FormDropdownField at :406, options from
 *     `SMALL_UPS_INPUT_PHASE_OPTIONS`
 *   - `smallUpsCapacity`             — NumberInput at :432, min 0 (numeric with
 *     `parseOptionalNumber`)
 *   - `smallUpsLoadPercentage`       — NumberInput at :452, min 0 / max 100
 *   - `powerRuntime`                 — FormInput (text) at :479
 *   - `smallUpsBatteryCabinetQty`    — NumberInput at :495, min 0 (integer)
 *   - `smallUpsMounting`             — FormDropdownField, options from
 *     `SMALL_UPS_MOUNTING_OPTIONS`
 *   - `smallUpsMountType`            — FormDropdownField, options from
 *     `SMALL_UPS_MOUNT_TYPE_OPTIONS` (Wall / Floor / Rack)
 *   - `smallUpsOutlets`              — FormInput (text)
 *   - `smallUpsMaintenanceBypassRequired` — FormYesNoChoice (boolean-backed)
 *   - `smallUpsExpectedLoadGrowth`   — NumberInput min 0 (numeric + request UOM)
 *   - `smallUpsLoadOnGenerator`      — FormYesNoChoice (boolean-backed; distinct
 *     from Backup Generator pairing)
 *   - `smallUpsRequiredSensors`      — FormInput multiline
 *   - `smallUpsBackupGenerator`      — FormYesNoChoice (boolean-backed
 *     via `booleanToYesNo`)
 *   - `powerSmallUpsAdditionalInfo`  — `RequestFormAdditionalInformation` at
 *     :579 (multilineText, maxLength 10000)
 */
interface SmallUpsFieldTypeSpec {
  type: SectionDescriptorFieldType;
  options?: readonly string[];
  validation?: SectionDescriptorFieldValidation;
}

const SMALL_UPS_FIELD_TYPES: Readonly<Record<string, SmallUpsFieldTypeSpec>> = {
  smallUpsManufacturer: { type: 'text' },
  smallUpsModel: { type: 'text' },
  powerSmallUpsDescription: {
    type: 'multilineText',
    validation: { maxLength: 10000 },
  },
  smallUpsSerialNumber: { type: 'text' },
  powerTypesOfUps: {
    type: 'dropdown',
    options: SMALL_UPS_TYPE_OPTIONS,
    validation: { options: SMALL_UPS_TYPE_OPTIONS },
  },
  smallUpsInputVoltage: { type: 'text' },
  smallUpsInputPlug: { type: 'text' },
  powerInputPowerPhase: {
    type: 'dropdown',
    options: SMALL_UPS_INPUT_PHASE_OPTIONS,
    validation: { options: SMALL_UPS_INPUT_PHASE_OPTIONS },
  },
  smallUpsCapacity: { type: 'number', validation: { min: 0 } },
  smallUpsLoadPercentage: { type: 'number', validation: { min: 0, max: 100 } },
  powerRuntime: { type: 'text' },
  smallUpsBatteryCabinetQty: { type: 'number', validation: { min: 0 } },
  smallUpsMounting: {
    type: 'dropdown',
    options: SMALL_UPS_MOUNTING_OPTIONS,
    validation: { options: SMALL_UPS_MOUNTING_OPTIONS },
  },
  smallUpsMountType: {
    type: 'dropdown',
    options: SMALL_UPS_MOUNT_TYPE_OPTIONS,
    validation: { options: SMALL_UPS_MOUNT_TYPE_OPTIONS },
  },
  smallUpsOutlets: { type: 'text' },
  smallUpsMaintenanceBypassRequired: { type: 'yesNo' },
  smallUpsExpectedLoadGrowth: { type: 'number', validation: { min: 0 } },
  smallUpsLoadOnGenerator: { type: 'yesNo' },
  smallUpsRequiredSensors: {
    type: 'multilineText',
    validation: { maxLength: 10000 },
  },
  smallUpsBackupGenerator: { type: 'yesNo' },
  // Per-unit "Additional Information" note (migration 091 per-instance column)
  powerSmallUpsAdditionalInfo: {
    type: 'multilineText',
    validation: { maxLength: 10000 },
  },
};

/** Registry lookup: dataField → RequestFieldDefinition (for label / subsection). */
const SMALL_UPS_FIELDS_BY_DATA_FIELD = new Map(
  SMALL_UPS_FIELDS.map(field => [field.dataField, field]),
);

/** Identity fields the engine list chrome owns; NOT in per-unit `fields`. */
const SMALL_UPS_IDENTITY_KEYS = new Set(['name', 'status']);

/**
 * Per-unit descriptor field dataField keys, in editor order. Sourced from
 * `SMALL_UPS_EDITOR_SUBSECTION_FIELDS` minus identity fields (`name`,
 * `status`), plus the note field (`powerSmallUpsAdditionalInfo`) appended.
 */
const PER_UNIT_DATA_FIELD_KEYS: readonly string[] = [
  ...SMALL_UPS_EDITOR_SUBSECTION_FIELDS.filter(
    field => !SMALL_UPS_IDENTITY_KEYS.has(field.key),
  ).map(field => field.key),
  'powerSmallUpsAdditionalInfo',
];

function buildDescriptorField(dataField: string): SectionDescriptorField {
  if (dataField === 'locationTagId') {
    return locationTagDescriptorField('small-ups', 'smallUps');
  }
  const registryField = SMALL_UPS_FIELDS_BY_DATA_FIELD.get(dataField);
  if (!registryField) {
    throw new Error(
      `SMALL_UPS_DESCRIPTOR: no SMALL_UPS_FIELDS entry for ${dataField}`,
    );
  }
  const spec = SMALL_UPS_FIELD_TYPES[dataField] ?? { type: 'text' };
  return {
    ...registryField,
    type: spec.type,
    options: spec.options,
    validation: spec.validation,
  };
}

/** Typed per-unit descriptor fields (requirements + note). */
const SMALL_UPS_DESCRIPTOR_FIELDS: readonly SectionDescriptorField[] =
  PER_UNIT_DATA_FIELD_KEYS.map(buildDescriptorField);

/**
 * Snake_case columns the Small UPS section persists — the exact key
 * set emitted by `small-ups-configuration.mapper.toEntity`. Migrations 091,
 * 116, and 124 add columns on `request_small_ups`. The
 * `descriptorDbBinding` guardrail joins this list to both the mapper keys and
 * the migration 091 + 116 + 124 column set.
 */
export const SMALL_UPS_DESCRIPTOR_DB_COLUMNS: readonly string[] = [
  'request_id',
  'status',
  'name',
  'location_tag_id',
  'small_ups_manufacturer',
  'small_ups_model',
  'power_small_ups_description',
  'small_ups_serial_number',
  'power_types_of_ups',
  'small_ups_input_voltage',
  'small_ups_input_plug',
  'power_input_power_phase',
  'small_ups_capacity',
  'small_ups_load_percentage',
  'power_runtime',
  'small_ups_battery_cabinet_qty',
  'small_ups_mounting',
  'small_ups_mount_type',
  'small_ups_outlets',
  'small_ups_maintenance_bypass_required',
  'small_ups_expected_load_growth',
  'small_ups_load_on_generator',
  'small_ups_required_sensors',
  'small_ups_backup_generator',
  'power_small_ups_additional_info',
  'display_order',
];

/**
 * Small UPS list projection (#997 Story 6.4 — parity by reuse). Delegates to the
 * product-approved bespoke table (`smallUpsListUtils`: NAME | MANUFACTURER |
 * CAPACITY | LOAD % | PHASE / PLUG + compact set, sublines, double-line cells).
 * Engine rows carry identity as `instanceName`/`instanceStatus` (stripped from
 * the Small UPS shape by `smallUpsToDescriptorInstance`), so adapt the row back
 * before the Small-UPS-shaped formatters run — the exact `rackRowForList` pattern.
 */
const smallUpsRowForList = (
  row: Record<string, unknown>,
): SmallUpsConfiguration => {
  const unit = row as unknown as SmallUpsConfiguration;
  return {
    ...unit,
    name: unit.name ?? (row.instanceName as SmallUpsConfiguration['name']),
    status:
      unit.status ?? (row.instanceStatus as SmallUpsConfiguration['status']),
  };
};

const SMALL_UPS_LIST_COLUMNS: SectionListColumnsProjection = {
  locationTagCellColumn: 'ROOM',
  columnsForLayout: smallUpsTableColumnsForLayout,
  headerLabel: (column, layout) =>
    smallUpsTableHeaderLabel(column as SmallUpsTableColumnKey, layout),
  formatCell: (row, column) =>
    formatSmallUpsTableCell(
      smallUpsRowForList(row),
      column as SmallUpsTableColumnKey,
    ),
  columnAlign: SMALL_UPS_TABLE_COLUMN_ALIGN,
  getColumnSubline: (row, column, layout) =>
    getSmallUpsColumnSubline(
      column as SmallUpsTableColumnKey,
      smallUpsRowForList(row),
      layout,
    ),
  columnUsesDoubleLine: (column, layout) =>
    smallUpsColumnUsesDoubleLine(column as SmallUpsTableColumnKey, layout),
};

/** The canonical Small UPS descriptor. */
export const SMALL_UPS_DESCRIPTOR: SectionDescriptor = {
  id: 'small-ups',
  stepId: 'small-ups',
  labels: {
    entitySingular: 'Small UPS',
    entityPlural: 'Small UPS',
    addButtonLabel: 'Add Small UPS',
    emptyStateTitle: SMALL_UPS_EMPTY_STATE_TITLE,
    editFormTitle: SMALL_UPS_EDIT_HEADER_TITLE_NEW,
    editFormSubtitle: SMALL_UPS_CONFIGURATIONS_LIST_TITLE,
    // Chrome copy parity (#997 Story 6.4, mirror Racks 6.3 R5 / GATE-5): bind the
    // bespoke hand-authored copy so the engine chrome is byte-identical to the
    // pre-swap subsection, not template-coincident.
    editSaveLabel: SMALL_UPS_EDIT_SAVE_LABEL,
    editSaveDraftLabel: SMALL_UPS_EDIT_SAVE_DRAFT_LABEL,
    editDeleteLabel: SMALL_UPS_EDIT_DELETE_LABEL,
    deleteConfirmTitle: SMALL_UPS_DELETE_CONFIRM_TITLE,
    deleteConfirmMessage: SMALL_UPS_DELETE_CONFIRM_MESSAGE,
    editAllListLabel: SMALL_UPS_EDIT_ALL_UNITS_LABEL,
    editDiscardTitle: SMALL_UPS_DISCARD_UNSAVED_TITLE,
    editDiscardMessage: SMALL_UPS_DISCARD_UNSAVED_MESSAGE,
    editDiscardConfirmLabel: SMALL_UPS_DISCARD_UNSAVED_DISCARD_LABEL,
    editDiscardKeepEditingLabel: SMALL_UPS_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
    newInstanceChromeTitle: SMALL_UPS_EDIT_HEADER_TITLE_NEW,
  },
  cardinality: 'many',
  fields: SMALL_UPS_DESCRIPTOR_FIELDS,
  permissionRef: {
    rbacAnchorDataField: 'smallUpsManufacturer',
  },
  summaryOrder: SMALL_UPS_DESCRIPTOR_FIELDS.map(field => field.dataField),
  pdfLayout: {
    stepId: 'small-ups',
    anchor: 'Small UPS Information',
    perUnitFieldGroupKeys: ['smallUpsInfoFields'],
  },
  // Small UPS has NO request-level shared fields — no analog to LC's
  // `lcLiquidToChipDesired`. Mirrors LU / PD.
  sharedFieldKeys: [],
  // The live SU editor renders TWO per-unit attachment carousels: the details
  // card at `SECTION_IDS.SMALL_UPS_REQUIREMENTS`
  // (`SmallUpsEditFormContent.tsx:157-186` via
  // `resolveRepeatableInstanceAttachmentSectionId`) AND the per-unit
  // Additional Information card at `SECTION_IDS.SMALL_UPS_ADDITIONAL`
  // (`SmallUpsEditFormContent.tsx:579-597` via the additional-section id
  // resolver). Both are declared here so the descriptor stays truthful for the
  // deferred live-editor swap follow-up. Ordering matters:
  // `DescriptorInstanceEditFormBody` derives the per-unit Additional Info
  // section id from `attachmentBaseSectionIds[length - 1]`, so
  // `SMALL_UPS_ADDITIONAL` MUST be last. This is the SECOND section to
  // exercise the two-entry case (PD 4-4 was first) — the semantics remain
  // unit-test-uncovered in this story (deferred to Phase 4 device sign-off).
  attachmentsEnabled: true,
  attachmentBaseSectionIds: [
    SECTION_IDS.SMALL_UPS_REQUIREMENTS,
    SECTION_IDS.SMALL_UPS_ADDITIONAL,
  ],
  listColumns: SMALL_UPS_LIST_COLUMNS,
  additionalInfoField: 'powerSmallUpsAdditionalInfo',
  offlineBinding: {
    restResource: 'small-ups',
    // Small UPS is the SECOND section wired onto the engine save/flush path
    // (#997 Story 6.4). It opts in explicitly via the delegating flush shim
    // `flushSmallUpsDescriptorEditSession` (see `resolveDelegatedFlushBinding`).
    delegatedFlush: true,
    reorderIdsBodyKey: 'smallUpsIds',
    persistedInstancesKey: 'smallUpsConfigurations',
    instanceIdOpField: 'smallUpsId',
    reorderIdsOpField: 'smallUpsIds',
  },
  dbBinding: {
    table: 'request_small_ups',
    columns: SMALL_UPS_DESCRIPTOR_DB_COLUMNS,
  },
};
